import { ChangeDetectionStrategy, Component, OnInit, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { filter, map, startWith } from 'rxjs';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

import { ChatInfoBoardComponent } from './components/chat-info-board/chat-info-board.component';
import { ChatMessagesListComponent } from './components/chat-messages-list/chat-messages-list.component';
import { ChatThreadsListComponent } from './components/chat-threads-list/chat-threads-list.component';
import { ChatThread } from './core/models/chat.interface';
import { ChatStateService } from './core/services/chat-state.service';

@Component({
  selector: 'chat-root',
  standalone: true,
  imports: [ReactiveFormsModule, LoaderComponent, ChatThreadsListComponent, ChatMessagesListComponent, ChatInfoBoardComponent],
  templateUrl: './chat.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  protected readonly chatState = inject(ChatStateService);

  protected readonly initialThreadsLoading = computed<boolean>(() => this.chatState.initialThreadsLoading());
  protected readonly hasSelectedThread = computed<boolean>(() => !!this.chatState.selectedThread());

  private readonly currentThreadId = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.url),
      startWith(this.router.url),
      map(url => this.extractThreadIdFromUrl(url))
    ),
    { initialValue: null }
  );

  private readonly threadIdEffect = effect(() => {
    if (this.currentThreadId()) {
      this.chatState.clearSelectedThread();
      this.chatState.messagesLoading.set(true);
      this.chatState.loadThreadData(this.currentThreadId()).then(() => this.chatState.messagesLoading.set(false));
    } else this.chatState.clearSelectedThread();
  });

  ngOnInit() {
    this.chatState.init();
  }

  protected onOpenThread($event: ChatThread) {
    this.router.navigate([$event.id], { relativeTo: this.route });
  }

  private extractThreadIdFromUrl(url: string): string | null {
    const urlParts = url.split('/');
    const threadsIndex = urlParts.findIndex(part => part === 'threads');

    return threadsIndex >= 0 && urlParts[threadsIndex + 1] ? urlParts[threadsIndex + 1] : null;
  }
}
